import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useFetch } from '../hooks/useFetch';
import { apiRequest } from '../services/apiClient';
import { withQuery } from '../utils/buildQuery';
import Button from '../components/ui/Button';
import WorkspacePage from '../components/layout/WorkspacePage';
import StatusBanner from '../components/ui/StatusBanner';
import AlertCard from '../components/ui/AlertCard';
import EmptyState from '../components/ui/EmptyState';
import { StatusDot, StatusStripe } from '../components/ui/Status';
import SkeletonBlock from '../components/ui/SkeletonBlock';
import { getScopeRollupLabel } from '../config/appRoutes';
import { DIRECTOR_DATA_UPDATED_EVENT } from '../utils/directorEvents';
import './AlertsCenter.css';

const ALL_LOCATIONS_CODE = 'ALL';

const CATEGORY_CHIPS = [
  { key: 'all', label: 'All' },
  { key: 'kpi-behind', label: 'KPIs behind' },
  { key: 'kpi-missing', label: 'Missing actuals' },
  { key: 'approval-aging', label: 'Approvals' },
  { key: 'attendance-risk', label: 'Attendance' }
];

/**
 * Alerts Center — proactively surfaces issues and wins using /api/alerts.
 *
 * Inherits the global location + period filters from App.js (same sticky
 * URL state as Executive Snapshot) so share-permalinks keep working.
 * Category chips are client-side filters over the already-ranked payload —
 * they never refetch.
 */
function AlertsCenter({
  selectedLocationCode = ALL_LOCATIONS_CODE,
  selectedPeriod = 'monthly',
  locationLabel = '',
  onNavigate,
  user = null
}) {
  const locationScope = selectedLocationCode && selectedLocationCode !== ALL_LOCATIONS_CODE
    ? selectedLocationCode
    : null;

  const url = useMemo(
    () => withQuery('/api/alerts', {
      locationCode: locationScope || undefined,
      period: selectedPeriod
    }),
    [locationScope, selectedPeriod]
  );

  const { data, isLoading, error, refetch } = useFetch(url, {
    deps: [locationScope, selectedPeriod]
  });

  useEffect(() => {
    const handleDirectorDataUpdated = () => refetch();
    window.addEventListener(DIRECTOR_DATA_UPDATED_EVENT, handleDirectorDataUpdated);
    return () => window.removeEventListener(DIRECTOR_DATA_UPDATED_EVENT, handleDirectorDataUpdated);
  }, [refetch]);

  const [categoryFilter, setCategoryFilter] = useState('all');
  const [winsOpen, setWinsOpen] = useState(false);

  const actions = useMemo(
    () => (Array.isArray(data?.actions) ? data.actions : []),
    [data]
  );
  const wins = useMemo(
    () => (Array.isArray(data?.wins) ? data.wins : []),
    [data]
  );
  const warnings = Array.isArray(data?.warnings) ? data.warnings : [];
  const truncated = data?.counts?.truncated || 0;

  const visibleActions = useMemo(() => {
    if (categoryFilter === 'all') return actions;
    return actions.filter(a => a.category === categoryFilter);
  }, [actions, categoryFilter]);

  const categoryCounts = useMemo(() => {
    const counts = { all: actions.length };
    for (const a of actions) {
      counts[a.category] = (counts[a.category] || 0) + 1;
    }
    return counts;
  }, [actions]);

  const headerLocation = locationLabel
    || (locationScope ? locationScope : (getScopeRollupLabel(user) || 'Company Rollup'));
  const periodLabel = capitalize(selectedPeriod);

  const [actionBusy, setActionBusy] = useState(false);
  const [actionBanner, setActionBanner] = useState('');
  const [actionError, setActionError] = useState('');
  const [dismissedOpen, setDismissedOpen] = useState(false);
  const [dismissedRows, setDismissedRows] = useState([]);
  const [dismissedLoaded, setDismissedLoaded] = useState(false);

  const fetchDismissed = useCallback(async () => {
    try {
      const data = await apiRequest('/api/alerts/dismissed');
      setDismissedRows(Array.isArray(data?.items) ? data.items : []);
      setDismissedLoaded(true);
    } catch (err) {
      setActionError(err?.message || 'Unable to load dismissed alerts.');
    }
  }, []);

  const handleDismiss = useCallback(async (alert) => {
    if (!alert?.id) return;
    setActionBusy(true);
    setActionError('');
    try {
      await apiRequest(`/api/alerts/${encodeURIComponent(alert.id)}/dismiss`, {
        method: 'POST',
        body: JSON.stringify({})
      });
      setActionBanner('Alert dismissed. Open "Dismissed alerts" below to restore it.');
      await refetch();
      if (dismissedLoaded) await fetchDismissed();
    } catch (err) {
      setActionError(err?.message || 'Unable to dismiss alert.');
    } finally {
      setActionBusy(false);
    }
  }, [refetch, fetchDismissed, dismissedLoaded]);

  const handleSnooze = useCallback(async (alert, options = {}) => {
    if (!alert?.id) return;
    const days = Number(options.days) > 0 ? Number(options.days) : 7;
    const until = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();
    setActionBusy(true);
    setActionError('');
    try {
      await apiRequest(`/api/alerts/${encodeURIComponent(alert.id)}/snooze`, {
        method: 'POST',
        body: JSON.stringify({ snoozeUntil: until })
      });
      setActionBanner(`Snoozed for ${days} day${days === 1 ? '' : 's'}.`);
      await refetch();
      if (dismissedLoaded) await fetchDismissed();
    } catch (err) {
      setActionError(err?.message || 'Unable to snooze alert.');
    } finally {
      setActionBusy(false);
    }
  }, [refetch, fetchDismissed, dismissedLoaded]);

  const handleRestore = useCallback(async (alertId) => {
    if (!alertId) return;
    setActionBusy(true);
    setActionError('');
    try {
      await apiRequest(`/api/alerts/${encodeURIComponent(alertId)}/restore`, {
        method: 'POST',
        body: JSON.stringify({})
      });
      setActionBanner('Alert restored.');
      await refetch();
      await fetchDismissed();
    } catch (err) {
      setActionError(err?.message || 'Unable to restore alert.');
    } finally {
      setActionBusy(false);
    }
  }, [refetch, fetchDismissed]);

  const toggleDismissedSection = useCallback(async () => {
    const next = !dismissedOpen;
    setDismissedOpen(next);
    if (next && !dismissedLoaded) {
      await fetchDismissed();
    }
  }, [dismissedOpen, dismissedLoaded, fetchDismissed]);

  const handleCtaClick = useCallback((cta) => {
    if (!cta?.path) return;
    if (typeof onNavigate === 'function') {
      onNavigate(cta.path);
      return;
    }
    // Fallback: direct navigation. Keeps the component useful even when
    // rendered outside the App shell.
    if (typeof window !== 'undefined') {
      window.location.assign(cta.path);
    }
  }, [onNavigate]);

  const generatedAt = useMemo(() => {
    if (!data?.generatedAt) return '';
    try {
      return new Date(data.generatedAt).toLocaleString([], {
        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
      });
    } catch {
      return '';
    }
  }, [data?.generatedAt]);

  return (
    <WorkspacePage
      className="exec-root alerts-root exec-page"
      data-testid="workspace-alerts"
      data-workspace-id="alerts"
      eyebrow="Alerts Center"
      title={`${headerLocation} · ${periodLabel}`}
      subtitle="Proactive signals ranked by severity — KPIs behind goal, missing actuals, aging approvals, and attendance risk. Wins appear at the bottom so your attention stays on action."
      actions={(
        <div className="exec-header-meta">
          <span className="exec-header-chip">
            <StatusDot tone={actions.length ? 'watch' : 'good'} className="exec-header-chip-dot" aria-hidden />
            {isLoading && !data ? (
              <SkeletonBlock as="span" width={120} height={12} radius="var(--radius-pill)" />
            ) : `${actions.length} action · ${wins.length} win${wins.length === 1 ? '' : 's'}`}
          </span>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => refetch()}
            loading={isLoading}
            loadingLabel="Refreshing…"
          >
            Refresh
          </Button>
        </div>
      )}
    >
        {error ? (
          <StatusBanner
            kind="error"
            message={error}
            onRetry={refetch}
            retryLabel="Try again"
          />
        ) : null}

        {warnings.length ? (
          <StatusBanner
            kind="warning"
            message={`Partial data — ${warnings.join(', ')}.`}
          />
        ) : null}

        <div className="alerts-chips" role="tablist" aria-label="Filter alerts by category">
          {CATEGORY_CHIPS.map(chip => {
            const count = categoryCounts[chip.key] || 0;
            const active = categoryFilter === chip.key;
            const disabled = chip.key !== 'all' && count === 0;
            return (
              <button
                key={chip.key}
                type="button"
                role="tab"
                aria-selected={active}
                disabled={disabled}
                className={`alerts-chip ${active ? 'is-active' : ''}`}
                onClick={() => setCategoryFilter(chip.key)}
              >
                {chip.label}
                <span className="alerts-chip-count">{count}</span>
              </button>
            );
          })}
        </div>

        {actionBanner ? <StatusBanner kind="success" message={actionBanner} live /> : null}
        {actionError ? <StatusBanner kind="error" message={actionError} /> : null}

        <section className="exec-section" aria-label="Action alerts">
          {isLoading && !data ? (
            <AlertSkeleton />
          ) : visibleActions.length ? (
            <div className="alerts-list">
              {visibleActions.map(alert => (
                <AlertCard
                  key={alert.id}
                  alert={alert}
                  onCtaClick={handleCtaClick}
                  onDismiss={handleDismiss}
                  onSnooze={handleSnooze}
                  busy={actionBusy}
                />
              ))}
            </div>
          ) : (
            <EmptyState
              icon="!"
              headline={categoryFilter === 'all' ? 'No action alerts' : 'No alerts in this category'}
              body={categoryFilter === 'all'
                ? 'Every KPI, approval, and attendance signal is healthy for this scope.'
                : 'Try another category or broaden the location and period filters.'}
              primaryCtaLabel="Refresh Alerts"
              onPrimaryCta={refetch}
            />
          )}
          {truncated > 0 && categoryFilter === 'all' ? (
            <p className="alerts-truncated">
              Showing the top {actions.length} of {actions.length + truncated}.
              Narrow the scope with the location or period filter to see the rest.
            </p>
          ) : null}
        </section>

        {wins.length ? (
          <section className="exec-section alerts-wins" aria-label="Wins and streaks">
            <button
              type="button"
              className="alerts-wins-toggle"
              aria-expanded={winsOpen}
              onClick={() => setWinsOpen(v => !v)}
            >
              <span className="alerts-wins-caret" aria-hidden>
                {winsOpen ? '▾' : '▸'}
              </span>
              Wins &amp; streaks
              <span className="alerts-wins-count">{wins.length}</span>
            </button>
            {winsOpen ? (
              <div className="alerts-list">
                {wins.map(alert => (
                  <AlertCard
                  key={alert.id}
                  alert={alert}
                  onCtaClick={handleCtaClick}
                  onDismiss={handleDismiss}
                  onSnooze={handleSnooze}
                  busy={actionBusy}
                />
                ))}
              </div>
            ) : null}
          </section>
        ) : null}

        <section className="exec-section alerts-dismissed" aria-label="Dismissed alerts">
          <button
            type="button"
            className="alerts-wins-toggle"
            aria-expanded={dismissedOpen}
            onClick={toggleDismissedSection}
            data-testid="alerts-dismissed-toggle"
          >
            <span className="alerts-wins-caret" aria-hidden>
              {dismissedOpen ? '▾' : '▸'}
            </span>
            Dismissed alerts
            {dismissedLoaded ? (
              <span className="alerts-wins-count">{dismissedRows.length}</span>
            ) : null}
          </button>
          {dismissedOpen ? (
            !dismissedLoaded ? (
              <p className="alerts-truncated">Loading dismissed alerts…</p>
            ) : dismissedRows.length === 0 ? (
              <p className="alerts-truncated">No dismissed alerts.</p>
            ) : (
              <ul className="alerts-dismissed-list" data-testid="alerts-dismissed-list">
                {dismissedRows.map(row => (
                  <li key={row.alertId} className="alerts-dismissed-row" data-testid={`alerts-dismissed-row-${row.alertId}`}>
                    <div className="alerts-dismissed-row-main">
                      <div className="alerts-dismissed-row-id">{row.alertId}</div>
                      <div className="alerts-dismissed-row-meta">
                        {row.category || 'alert'} · {row.isSnoozed
                          ? `snoozed until ${String(row.dismissedUntil).slice(0, 10)}`
                          : 'dismissed permanently'}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => handleRestore(row.alertId)}
                      disabled={actionBusy}
                      data-testid={`alerts-dismissed-restore-${row.alertId}`}
                    >
                      Restore
                    </Button>
                  </li>
                ))}
              </ul>
            )
          ) : null}
        </section>

        <footer className="exec-footer">
          <span>Live operational records · {headerLocation}</span>
          <span>{generatedAt ? `Generated ${generatedAt}` : ''}</span>
        </footer>
    </WorkspacePage>
  );
}

function AlertSkeleton() {
  return (
    <div className="alerts-list">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="alert-card alert-card--info" aria-hidden>
          <StatusStripe tone="neutral" className="alert-card-rail" aria-hidden />
          <div className="alert-card-body">
            <SkeletonBlock width="30%" height={10} />
            <SkeletonBlock width="60%" height={14} />
            <SkeletonBlock width="45%" height={10} />
          </div>
          <div className="alert-card-side">
            <SkeletonBlock width={60} height={14} />
          </div>
        </div>
      ))}
    </div>
  );
}

function capitalize(value) {
  if (!value) return '';
  return String(value).charAt(0).toUpperCase() + String(value).slice(1);
}

export default AlertsCenter;
